import * as ExcelJS from 'exceljs';

/**
 * Lightweight interfaces describing the subset of the ExcelJS API used by the helpers.
 * These make the functions easy to unit test with simple mocks without pulling ExcelJS internals.
 */
export interface CellLike {
  value: unknown;
}

export interface RowLike {
  getCell: (colNumber: number) => CellLike;
}

export interface WorksheetLike {
  rowCount: number;
  columnCount: number;
  getRow: (rowNumber: number) => RowLike;
  getCell: (rowNumber: number, colNumber: number) => CellLike;
}

export interface ExcelRowData {
  [key: string]: string | number | null;
}

/**
 * Extracts header names from the first row of the worksheet.
 * - Uses the cell text when present, otherwise falls back to `Column_{n}`.
 * - Guarantees uniqueness by suffixing duplicates with an incrementing counter.
 *
 * @param worksheet - ExcelJS worksheet or a minimal compatible worksheet
 * @returns An ordered array of header names, one for each column in the sheet
 */
export const extractHeadersFromWorksheet = (
  worksheet: WorksheetLike | ExcelJS.Worksheet
): string[] => {
  const headerRow = worksheet.getRow(1);
  const totalColumns = worksheet.columnCount;

  const seenHeaders = new Map<string, number>();
  const headers: string[] = [];

  for (let colNumber = 1; colNumber <= totalColumns; colNumber++) {
    const headerCell = headerRow.getCell(colNumber);
    const baseHeader = (headerCell.value?.toString() || '').trim();
    const fallbackHeader = `Column_${colNumber}`;
    const rawHeader = baseHeader !== '' ? baseHeader : fallbackHeader;

    const count = seenHeaders.get(rawHeader) || 0;
    const uniqueHeader = count === 0 ? rawHeader : `${rawHeader}_${count + 1}`;
    seenHeaders.set(rawHeader, count + 1);
    headers.push(uniqueHeader);
  }

  return headers;
};

/**
 * Parses the worksheet into JSON rows using the provided headers.
 * - Iterates across all columns so empty cells are preserved as nulls.
 * - Skips entirely empty rows (all values null or empty string).
 *
 * @param worksheet - ExcelJS worksheet or a minimal compatible worksheet
 * @param headers - The ordered headers to use for column mapping
 * @returns An array of row objects keyed by header names
 */
export const parseWorksheetRows = (
  worksheet: WorksheetLike | ExcelJS.Worksheet,
  headers: string[]
): ExcelRowData[] => {
  const totalRows = worksheet.rowCount;
  const totalColumns = headers.length;
  const rows: ExcelRowData[] = [];

  for (let rowNumber = 2; rowNumber <= totalRows; rowNumber++) {
    const rowData: ExcelRowData = {};
    for (let colNumber = 1; colNumber <= totalColumns; colNumber++) {
      const header = headers[colNumber - 1];
      const cell = worksheet.getCell(rowNumber, colNumber);
      const value = cell?.value;
      const normalizedValue =
        value === undefined || value === null
          ? null
          : (value as string | number);
      rowData[header] = normalizedValue;
    }
    const hasAnyValue = Object.values(rowData).some(
      (v) => v !== null && v !== ''
    );
    if (hasAnyValue) {
      rows.push(rowData);
    }
  }

  return rows;
};

/**
 * Normalizes a list of raw cell values to a cleaned string list.
 * - Trims whitespace
 * - Filters out empty/whitespace-only values
 * - Deduplicates while preserving first occurrence order
 *
 * This is useful for aggregating values coming from multiple mapped columns
 * (e.g., Phone 1, Phone 2, ... all mapped to the same target field).
 *
 * @param values - Heterogeneous list of raw values parsed from cells
 * @returns Clean, unique, ordered string list
 */
export const normalizeStringList = (
  values: ReadonlyArray<string | number | null | undefined>
): string[] => {
  const result: string[] = [];
  const seen = new Set<string>();

  for (const value of values) {
    if (value === null || value === undefined) {
      continue;
    }

    const cleanedValue = String(value).trim();

    if (cleanedValue === '') {
      continue;
    }

    if (seen.has(cleanedValue)) {
      continue;
    }

    seen.add(cleanedValue);
    result.push(cleanedValue);
  }

  return result;
};
